import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { roleFor } from "@/lib/roles";
import {
  Inbox, FileText, CalendarClock, Wrench, Receipt, Crown, ShieldAlert, DollarSign,
  BellRing, ArrowRight, Loader2, TrendingUp,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

const CARD = (s) => ({
  revenue: { label: "Revenue Collected", value: money(s.revenue_collected), sub: `${money(s.invoices_outstanding_value)} outstanding`, icon: DollarSign, tone: "text-green-600", to: "invoices" },
  open_proposals: { label: "Open Proposals", value: s.proposals_open, sub: `${s.proposals_sold} sold`, icon: FileText, tone: "text-blue-600", to: "launch-select" },
  booked_jobs: { label: "Booked Jobs", value: s.jobs_booked, sub: `${s.jobs_unscheduled} unscheduled`, icon: Wrench, tone: "text-orange-600", to: "dispatch" },
  outstanding_invoices: { label: "Outstanding Invoices", value: s.invoices_outstanding_count, sub: money(s.invoices_outstanding_value), icon: Receipt, tone: "text-red-600", to: "invoices" },
  memberships: { label: "Comfort Club", value: s.memberships_count, sub: `${money(s.mrr)} MRR`, icon: Crown, tone: "text-purple-600", to: "membership" },
  warranty: { label: "Warranty At-Risk", value: s.warranty_at_risk, sub: "overdue maintenance", icon: ShieldAlert, tone: "text-amber-600", to: "warranty" },
  new_leads: { label: "New Leads", value: s.leads_new, sub: `${s.leads_total} total`, icon: Inbox, tone: "text-cool", to: "leads" },
  unscheduled_jobs: { label: "Unscheduled Jobs", value: s.jobs_unscheduled, sub: "need dispatch", icon: Wrench, tone: "text-orange-600", to: "dispatch" },
  jobs_today: { label: "Jobs Today", value: (s.jobs_today || []).length, sub: "scheduled today", icon: CalendarClock, tone: "text-orange-600", to: "dispatch" },
});

const STAT_ONLY = new Set(["revenue", "open_proposals", "booked_jobs", "outstanding_invoices", "memberships", "warranty", "new_leads", "unscheduled_jobs", "jobs_today"]);

export default function RoleDashboard({ roleKey, go }) {
  const role = roleFor(roleKey);
  const { data: s, isLoading } = useQuery({ queryKey: ["dash-summary"], queryFn: async () => (await api.get("/dashboard/summary")).data });

  if (isLoading || !s) return <div className="flex items-center justify-center py-24 text-slate-400" data-testid="role-dashboard-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const cards = CARD(s);
  const statCards = role.widgets.filter((w) => STAT_ONLY.has(w));
  const showFollowups = role.widgets.includes("followups_due");
  const showUpcoming = role.widgets.includes("upcoming_appointments");
  const showJobsList = role.widgets.includes("jobs_today");
  const showLeadsList = role.widgets.includes("new_leads");

  return (
    <div data-testid="role-dashboard">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h1 className="text-2xl font-bold text-navy">{role.label} Dashboard</h1>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-navy/5 text-navy border border-navy/10">Role View</span>
      </div>
      <p className="text-slate-500 mb-6">{role.blurb}</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((w) => {
          const c = cards[w];
          const Icon = c.icon;
          return (
            <button key={w} onClick={() => go(c.to)} data-testid={`role-card-${w}`}
              className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-cool transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500">{c.label}</span>
                <Icon className={`h-4 w-4 ${c.tone}`} />
              </div>
              <p className="mt-2 text-2xl font-extrabold text-navy">{c.value ?? 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {showJobsList && (
          <ListCard title="Jobs Today" testid="role-list-jobs" empty="No jobs scheduled for today." to={() => go("dispatch")}
            items={(s.jobs_today || []).map((j) => ({ key: j.id, main: j.customer || "—", meta: `${j.service || "Service"} · ${j.tech || "Unassigned"}`, tag: j.status }))} />
        )}
        {showUpcoming && (
          <ListCard title="Upcoming Appointments" testid="role-list-appts" empty="No upcoming appointments." to={() => go("calendar")}
            items={(s.appointments_upcoming || []).map((a) => ({ key: a.id, main: a.customer || "—", meta: a.job_type || "Appointment", tag: a.scheduled_date }))} />
        )}
        {showLeadsList && (
          <ListCard title="Recent Leads" testid="role-list-leads" empty="No leads yet." to={() => go("leads")}
            items={(s.leads_recent || []).map((l) => ({ key: l.id, main: l.name || "—", meta: l.service || "General", tag: l.status }))} />
        )}
        {showFollowups && (
          <ListCard title="Follow-Ups Due" testid="role-list-followups" empty="No follow-ups due this week." icon={BellRing} to={() => go("customers")}
            items={(s.followups_due || []).map((f) => ({ key: f.id, main: f.title || "Follow-up", meta: "", tag: f.due_at }))} />
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {role.quick.map((q) => (
          <button key={q.to} onClick={() => go(q.to)} data-testid={`role-quick-${q.to}`}
            className="inline-flex items-center gap-1.5 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-navy/90">
            {q.label} <ArrowRight className="h-4 w-4" />
          </button>
        ))}
      </div>
    </div>
  );
}

function ListCard({ title, items, empty, testid, to, icon: Icon = TrendingUp }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-navy flex items-center gap-2"><Icon className="h-4 w-4 text-cool" /> {title}</h3>
        {to && <button onClick={to} className="text-xs font-semibold text-[#F97316] hover:underline">View all</button>}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">{empty}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it) => (
            <li key={it.key} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{it.main}</p>
                {it.meta && <p className="text-xs text-slate-400 truncate">{it.meta}</p>}
              </div>
              {it.tag && <span className="text-xs font-medium text-slate-500 shrink-0">{it.tag}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
